import { useEffect, useState } from 'react';

import { ArrowBack, CloseRounded, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  Paper,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { UserGroup } from '../../types';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';
import { MultiEmailInput } from '../uiComponents/multiEmailInput';
import { SearchBar } from '../uiComponents/searchBar';
import { TabPanel } from '../uiComponents/tabPanel';
import { GroupList } from './UserDrawerSelectorGroupsList';
import { UserList } from './UserDrawerSelectorUsersList';

const customStyles: Record<string, SxProps<Theme>> = {
  drawerBox: {
    flexDirection: 'column',
    display: 'flex',
    height: '100%',
    flex: 1,
    overflow: 'hidden',
  },
  contentBox: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'row',
  },
  mainContentColumn: {
    padding: 2,
    flex: 1,
  },
  headerBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    marginBottom: '8px',
    paddingBottom: '0px',
    flexShrink: 0,
  },
  backButton: {
    display: 'flex',
    gap: 1,
    color: '#3E8FFA',
    cursor: 'pointer',
  },
  backButtonText: {
    fontSize: '14px',
    lineHeight: '24px',
    fontWeight: 400,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  title: {
    textAlign: 'left',
    fontSize: '20px',
    lineHeight: '32px',
    fontWeight: 500,
  },
  searchBox: {
    marginY: '8px',
  },
  tabs: {
    '&.MuiTabs-root': {
      minHeight: '34px',
    },
    '& .MuiTab-root': {
      paddingTop: '2px',
      paddingBottom: '2px',
      minHeight: '34px',
      textTransform: 'none',
    },
    '& .MuiTabs-indicator': {
      height: '3px',
      borderRadius: '20px',
    },
  },
  tabsBox: {
    borderBottom: 1,
    borderColor: 'divider',
  },
  divider: {
    height: '100%',
    alignSelf: 'stretch',
    flexShrink: 0,
  },
  closeButton: {
    color: '#666',
  },
  externalUsersContainer: {
    marginTop: '12px',
    padding: '16px 14px',
    borderRadius: '16px',
  },
  externalUsersLabel: {
    marginBottom: '8px',
    fontSize: '14px',
  },
  externalUsersDescription: {
    fontSize: '12px',
    color: '#6E7178',
    marginTop: '5px',
    fontWeight: 300,
  },
  locationCheckbox: {
    marginTop: '14px',
    marginLeft: '0px',
  },
  locationCheckboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  locationCheckboxTooltip: {
    fontSize: '16px',
  },
  locationTooltipList: {
    margin: '0px',
    padding: '0px',
  },
  locationTooltipListItem: {
    padding: '4px 0',
    display: 'list-item',
    listStyleType: 'disc',
    listStylePosition: 'inside',
    color: 'white',
  },
  drawerColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
};

interface User {
  id: number;
  name: string | null;
  email: string;
}
interface UserDrawerContentSelectorProps {
  onClose: () => void;
  groups: UserGroup[];
  users: User[];
  onSave: (selectedUsers: User[], externalUsers?: string[], isLocationActive?: boolean) => void;
  initialSelectedUsers?: User[];
  initialExternalUsers?: string[];
  initialLocationChecked?: boolean;
  singleUser?: boolean;
  isUserAssigner?: boolean;
  title: string;
  onBack?: () => void;
  onSignersUpdate?: (
    selectedUsers: User[],
    externalUsers: string[],
    isLocationActive: boolean,
  ) => void;
}

enum TabIndex {
  Users = 0,
  Groups = 1,
}

export function UserDrawerContentSelector({
  onClose,
  groups,
  users,
  onSave,
  initialSelectedUsers,
  initialExternalUsers,
  initialLocationChecked,
  singleUser,
  isUserAssigner,
  title,
  onBack,
  onSignersUpdate,
}: UserDrawerContentSelectorProps) {
  const [selectedTab, setSelectedTab] = useState(TabIndex.Users);
  const [selectedUsers, setSelectedUsers] = useState<User[]>(initialSelectedUsers || []);
  const [groupPreview, setGroupPreview] = useState<UserGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [externalUsers, setExternalUsers] = useState<string[]>(initialExternalUsers || []);
  const [isLocationActive, setIsLocationActive] = useState(initialLocationChecked);
  const { checkFlag } = useFeatureFlags();
  const lang = useLanguage();

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabIndex) => {
    setSelectedTab(newValue);
    setGroupPreview(null);
  };

  const areAllGroupUsersSelected = (group: UserGroup) => {
    return group.users.every((groupUser) =>
      selectedUsers.some((selectedUser) => selectedUser.id === groupUser.mobile_user_id),
    );
  };

  const handleGroupSelect = (group: UserGroup) => {
    if (areAllGroupUsersSelected(group)) {
      setSelectedUsers(
        selectedUsers.filter(
          (selectedUser) =>
            !group.users.some((groupUser) => groupUser.mobile_user_id === selectedUser.id),
        ),
      );
    } else {
      const selectedGroupUsers = group.users.filter(
        (groupUser) =>
          !selectedUsers.some((selectedUser) => selectedUser.id === groupUser.mobile_user_id),
      );
      setSelectedUsers([
        ...selectedUsers,
        ...users.filter((user) =>
          selectedGroupUsers.some((groupUser) => groupUser.mobile_user_id === user.id),
        ),
      ]);
    }
  };

  const handleUserToggle = (user: User) => {
    if (singleUser) {
      setSelectedUsers([user]);
      return;
    }
    const updatedUsers = selectedUsers.some((selectedUser) => selectedUser.id === user.id)
      ? selectedUsers.filter((selectedUser) => selectedUser.id !== user.id)
      : [...selectedUsers, user];

    setSelectedUsers(updatedUsers);
  };

  const handleAllUsersToggle = (usersSubList: User[]) => {
    const { unselectedUsersFromSubList, selectedUsersFromSubList } = usersSubList.reduce(
      (acc, user) => {
        if (selectedUsers.some((newSelectedUser) => newSelectedUser.id === user.id)) {
          acc.selectedUsersFromSubList.push(user);
        } else {
          acc.unselectedUsersFromSubList.push(user);
        }
        return acc;
      },
      { unselectedUsersFromSubList: [], selectedUsersFromSubList: [] },
    );

    if (unselectedUsersFromSubList.length > 0) {
      setSelectedUsers([...selectedUsers, ...unselectedUsersFromSubList]);
    } else {
      setSelectedUsers([
        ...selectedUsers.filter(
          (selectedUser) =>
            !selectedUsersFromSubList.some(
              (selectedUserFromSublist) => selectedUserFromSublist.id === selectedUser.id,
            ),
        ),
      ]);
    }
  };

  const handleBack = () => {
    onSave(selectedUsers, externalUsers, isLocationActive);
    onBack?.();
  };

  const handleGroupPreviewSelect = (group: UserGroup) => {
    groupPreview === group ? setGroupPreview(null) : setGroupPreview(group);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().trim().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredGroups = groups.filter((group) =>
    group.name?.toLowerCase().trim().includes(searchQuery.toLowerCase()),
  );

  const groupUsersPreview = groupPreview
    ? users.filter((user) =>
        groupPreview.users.some((groupUser) => groupUser.mobile_user_id === user.id),
      )
    : [];
  useEffect(() => {
    if (onSignersUpdate) {
      onSignersUpdate(selectedUsers, externalUsers, isLocationActive ?? false);
    }
  }, [externalUsers, isLocationActive, selectedUsers]);

  return (
    <>
      <Box
        sx={customStyles.drawerBox}
        width={groupPreview ? 800 : 440}
      >
        <Box sx={customStyles.headerBox}>
          {onBack && (
            <Box
              sx={customStyles.backButton}
              onClick={handleBack}
            >
              <ArrowBack />
              <Typography sx={customStyles.backButtonText}>
                {lang.formSignatureBuilder.changeSelection}
              </Typography>
            </Box>
          )}
          {!singleUser && (
            <IconButton
              onClick={onClose}
              sx={customStyles.closeButton}
            >
              <CloseRounded />
            </IconButton>
          )}
        </Box>
        <Box sx={customStyles.contentBox}>
          <Box sx={customStyles.mainContentColumn}>
            <Box sx={customStyles.titleContainer}>
              <Typography sx={customStyles.title}>{title}</Typography>
              {singleUser && (
                <IconButton
                  onClick={onClose}
                  sx={customStyles.closeButton}
                >
                  <CloseRounded />
                </IconButton>
              )}
            </Box>
            {isUserAssigner && (
              <Box>
                <Paper sx={customStyles.externalUsersContainer}>
                  <InputLabel sx={customStyles.externalUsersLabel}>
                    {lang.formSignatureBuilder.externalUsers}
                  </InputLabel>
                  <MultiEmailInput
                    hideLabel
                    validateEmailInUse
                    emails={externalUsers}
                    onEmailChange={setExternalUsers}
                  />
                  <Typography sx={customStyles.externalUsersDescription}>
                    {lang.formSignatureBuilder.externalUsersDescription}
                  </Typography>
                </Paper>
                {checkFlag('SignatureLocation') && !checkFlag('DynamicSignature') && (
                  <FormControlLabel
                    sx={customStyles.locationCheckbox}
                    control={<Checkbox inputProps={{ 'aria-label': 'controlled' }} />}
                    checked={isLocationActive}
                    onChange={() => setIsLocationActive(!isLocationActive)}
                    label={
                      <Box sx={customStyles.locationCheckboxLabel}>
                        {lang.formSignatureBuilder.locationConnection}
                        <Tooltip
                          title={
                            <List sx={customStyles.locationTooltipList}>
                              <ListItem sx={customStyles.locationTooltipListItem}>
                                {lang.formSignatureBuilder.locationConnectionTooltip1}
                              </ListItem>
                              <ListItem sx={customStyles.locationTooltipListItem}>
                                {lang.formSignatureBuilder.locationConnectionTooltip2}
                              </ListItem>
                            </List>
                          }
                        >
                          <InfoOutlined sx={customStyles.locationCheckboxTooltip} />
                        </Tooltip>
                      </Box>
                    }
                  />
                )}
              </Box>
            )}
            <Box sx={customStyles.searchBox}>
              <SearchBar
                placeholder={lang.formSignatureBuilder.searchPlaceholder}
                onSearch={(query) => setSearchQuery(query)}
                value={searchQuery}
              />
            </Box>
            <Box sx={customStyles.tabsBox}>
              <Tabs
                sx={customStyles.tabs}
                value={selectedTab}
                onChange={handleTabChange}
              >
                <Tab
                  label={lang.formSignatureBuilder.usersTab}
                  value={TabIndex.Users}
                />
                <Tab
                  label={lang.formSignatureBuilder.groupsTab}
                  value={TabIndex.Groups}
                />
              </Tabs>
            </Box>
            <TabPanel
              value={selectedTab}
              index={1}
              woPadding={true}
            >
              <GroupList
                groups={filteredGroups}
                selectedUsers={selectedUsers}
                onGroupSelect={handleGroupSelect}
                onGroupPreviewSelect={handleGroupPreviewSelect}
                groupPreview={groupPreview}
                hideCheckbox={singleUser}
              />
            </TabPanel>
            <TabPanel
              value={selectedTab}
              index={0}
              woPadding={true}
            >
              <UserList
                users={filteredUsers}
                selectedUsers={selectedUsers}
                onUserToggle={handleUserToggle}
                onAllUsersToggle={() => handleAllUsersToggle(filteredUsers)}
                singleUser={singleUser}
              />
            </TabPanel>
          </Box>
          {groupPreview && (
            <>
              <Box>
                <Divider
                  orientation='vertical'
                  sx={customStyles.divider}
                />
              </Box>
              <Box sx={customStyles.mainContentColumn}>
                <Typography sx={customStyles.title}>{groupPreview.name}</Typography>
                <UserList
                  users={groupUsersPreview}
                  selectedUsers={selectedUsers}
                  onUserToggle={(user) => handleUserToggle(user)}
                  onAllUsersToggle={() => handleAllUsersToggle(groupUsersPreview)}
                  singleUser={singleUser}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
}

export default UserDrawerContentSelector;
